// HFLedger/TransactionList.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TransactionForm from './TransactionForm';
import './TransactionList.css';

function TransactionList({ user, language, SERVER_ROOT, settings }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null); 
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [transactionReceipts, setTransactionReceipts] = useState({});
  
  // 필터 상태
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    accountId: '',
    categoryId: '',
    type: ''
  });
  
  // 정렬 상태
  const [sort, setSort] = useState({
    field: 'transaction_date',
    direction: 'desc'
  });
  
  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // 계좌 및 카테고리 목록
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  // 데이터 불러오기 
  useEffect(() => {
    const fetchTransactionsData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // 계좌, 카테고리 목록 가져오기
        const [accountsResponse, categoriesResponse] = await Promise.all([
          axios.get(`${SERVER_ROOT}/api/hfl/accounts/${user.uid}`),
          axios.get(`${SERVER_ROOT}/api/hfl/categories/${user.uid}`)
        ]);
        
        setAccounts(accountsResponse.data);
        setCategories(categoriesResponse.data);
        
        // 거래 내역 가져오기
        const queryString = buildQueryString();
        const transactionsResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/transactions${queryString}`
        );
        
        const transactionsList = transactionsResponse.data;
        setTransactions(transactionsList);
        setTotalCount(transactionsList.length);
        
        // 영수증 정보 가져오기
        const receiptsData = {};
        await Promise.all(
          transactionsList.map(async (transaction) => {
            try {
              const receiptResponse = await axios.get(
                `${SERVER_ROOT}/api/hfl/receipts/transaction/${transaction.transaction_id}`
              );
              if (receiptResponse.data && receiptResponse.data.length > 0) {
                receiptsData[transaction.transaction_id] = receiptResponse.data;
              }
            } catch (err) {
              console.error(`Error fetching receipts for transaction ${transaction.transaction_id}:`, err);
            }
          })
        );
        
        setTransactionReceipts(receiptsData);
        
      } catch (err) {
        console.error('Error fetching transactions data:', err);
        setError(err.message || '거래 내역을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTransactionsData();
  }, [user, SERVER_ROOT, currentPage, pageSize, filters, sort]);

  // 영수증 보기 핸들러 
  const handleViewReceipt = (transactionId) => {
    const receipts = transactionReceipts[transactionId] || [];
    if (receipts.length > 0) {
      setCurrentReceipt({
        transactionId,
        receipts
      });
      setShowReceiptModal(true);
    } else {
      alert(language === 'KR' ? '이 거래에 등록된 영수증이 없습니다.' : 'No receipts found for this transaction.');
    }
  };

  // 쿼리 스트링 빌드 함수
  const buildQueryString = () => {
    const params = new URLSearchParams();
    
    params.append('user_id', user.uid);
    params.append('limit', pageSize);
    params.append('offset', (currentPage - 1) * pageSize);
    
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.accountId) params.append('account_id', filters.accountId);
    if (filters.categoryId) params.append('category_id', filters.categoryId);
    if (filters.type) params.append('type', filters.type);
    
    return `?${params.toString()}`;
  };

  // 필터 변경 핸들러
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // 필터 변경시 첫 페이지로
  };

  // 정렬 변경 핸들러
  const handleSortChange = (field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // 거래 삭제 핸들러
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm(language === 'KR' ? '이 거래를 삭제하시겠습니까?' : 'Are you sure you want to delete this transaction?')) {
      return;
    }
    
    try {
      await axios.delete(`${SERVER_ROOT}/api/hfl/transactions/${transactionId}`);
      
      // 목록에서 제거
      setTransactions(prev => prev.filter(t => t.transaction_id !== transactionId));
      
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert(language === 'KR' ? '거래 삭제 중 오류가 발생했습니다.' : 'Error deleting transaction.');
    }
  };

  // 거래 수정 핸들러
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowForm(true);
  };

  // 폼 제출 완료 핸들러
  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingTransaction(null);
    // 목록 새로고침
    const fetchTransactionsData = async () => {
      try {
        const queryString = buildQueryString();
        const response = await axios.get(`${SERVER_ROOT}/api/hfl/transactions${queryString}`);
        setTransactions(response.data);
      } catch (err) {
        console.error('Error refreshing transactions:', err);
      }
    };
    fetchTransactionsData();
  };

  // 필터 초기화 핸들러
  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      accountId: '',
      categoryId: '',
      type: ''
    });
    setCurrentPage(1);
  };

  // 통화 포맷팅 헬퍼 함수
  const formatCurrency = (amount, currency = settings.default_currency) => {
    return new Intl.NumberFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // 날짜 포맷팅 헬퍼 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="transactions-loading">
        <div className="transactions-spinner"></div>
        <p>{language === 'KR' ? '거래 내역 로딩 중...' : 'Loading transactions...'}</p>
      </div>
    );
  }

  if (error && transactions.length === 0) {
    return (
      <div className="transactions-error">
        <h3>{language === 'KR' ? '데이터 로딩 오류' : 'Error Loading Data'}</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="transactions-button"
        >
          {language === 'KR' ? '새로고침' : 'Refresh'}
        </button>
      </div>
    );
  }

  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <h2>{language === 'KR' ? '거래 내역' : 'Transactions'}</h2>
      </div>
      
      {/* 필터 패널 */} 
      <div className="transactions-filters">
        <div className="filter-row">
          
          {/* 날짜 필터 (한 줄에 표시) */}
          <div className="date-filter-group">
            <div className="filter-group date-filters">
              <label>{language === 'KR' ? '시작일' : 'Start Date'}</label>
              <input 
                type="date" 
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-group date-filters">
              <label>{language === 'KR' ? '종료일' : 'End Date'}</label>
              <input 
                type="date" 
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          
          {/* 계좌, 카테고리, 유형 필터 (한 줄에 표시) */}
          <div className="other-filter-group">
            <div className="filter-group">
              <label>{language === 'KR' ? '계좌' : 'Account'}</label>
              <select 
                name="accountId"
                value={filters.accountId}
                onChange={handleFilterChange}
              >
                <option value="">{language === 'KR' ? '전체' : 'All'}</option>
                {accounts.map(account => (
                  <option key={account.account_id} value={account.account_id}>
                    {account.account_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>{language === 'KR' ? '카테고리' : 'Category'}</label>
              <select 
                name="categoryId"
                value={filters.categoryId}
                onChange={handleFilterChange}
              >
                <option value="">{language === 'KR' ? '전체' : 'All'}</option>
                {categories.map(category => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>{language === 'KR' ? '유형' : 'Type'}</label>
              <select 
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">{language === 'KR' ? '전체' : 'All'}</option>
                <option value="income">{language === 'KR' ? '수입' : 'Income'}</option>
                <option value="expense">{language === 'KR' ? '지출' : 'Expense'}</option>
              </select>
            </div>
          </div>
          
          {/* <button 
            className="reset-filters-button"
            onClick={handleResetFilters}
          >
            {language === 'KR' ? '필터 초기화' : 'Reset'}
          </button> */}
        </div>
      </div>
      
      {/* 거래 목록 테이블 */}
      <div className="transactions-table-container">
        <table className="transactions-table"> 
          <thead>
            <tr>
              <th 
                className={sort.field === 'transaction_date' ? `sorted-${sort.direction}` : ''}
                onClick={() => handleSortChange('transaction_date')}
              >
                {language === 'KR' ? '날짜' : 'Date'}
              </th>
              <th>{language === 'KR' ? '계좌' : 'Account'}</th>
              <th 
                className={sort.field === 'amount' ? `sorted-${sort.direction}` : ''}
                onClick={() => handleSortChange('amount')}
              >
                {language === 'KR' ? '금액' : 'Amount'}
              </th>
              <th>{language === 'KR' ? '카테고리' : 'Category'}</th>
              <th>{language === 'KR' ? '영수증' : 'Receipt'}</th> {/* 영수증 열 추가 */}
              <th>{language === 'KR' ? '설명' : 'Description'}</th>
              <th>{language === 'KR' ? '위치' : 'Location'}</th>
              
              
              <th>{language === 'KR' ? '수정/삭제' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {language === 'KR' ? '거래 내역이 없습니다.' : 'No transactions found.'}
                </td>
              </tr>
            ) : (
              transactions.map(transaction => (
                <tr key={transaction.transaction_id} className={transaction.type}>
                <td>{formatDate(transaction.transaction_date)}</td>
                <td>{transaction.account_name}</td>
                <td className={`amount-cell ${transaction.type}`}>
                  {transaction.type === 'income' ? '+' : '-'} 
                  {formatCurrency(transaction.amount, transaction.currency)}
                </td>
                <td>
                  <span 
                    className="category-badge"
                    style={{ backgroundColor: transaction.category_color }}
                  >
                    {transaction.category_name}
                  </span>
                </td>
                <td className="receipt-cell">
                  {/* 영수증 버튼 추가 */}
                  {transactionReceipts[transaction.transaction_id] ? (
                    <button 
                      className="view-receipt-button"
                      onClick={() => handleViewReceipt(transaction.transaction_id)}
                      title={language === 'KR' ? '영수증 보기' : 'View Receipt'}
                    > 
                      {language === 'KR' ? '영수증 보기' : 'View Receipt'}
                    </button>
                  ) : (
                    <span className="no-receipt">
                      {language === 'KR' ? '-' : '-'}
                    </span>
                  )}
                </td>
                <td className="description-cell">
                  {transaction.description || (language === 'KR' ? '(설명 없음)' : '(No description)')}
                </td>
                <td className="location-cell">
                  {transaction.location || (language === 'KR' ? '(위치 없음)' : '(No location)')}
                </td>
                
                
                <td className="actions-cell">
                  <button 
                    className="edit-button"
                    onClick={() => handleEditTransaction(transaction)}
                    title={language === 'KR' ? '수정' : 'Edit'}
                  >
                    {language === 'KR' ? '수정' : 'Edit'}
                  </button>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteTransaction(transaction.transaction_id)}
                    title={language === 'KR' ? '삭제' : 'Delete'}
                  >
                    {language === 'KR' ? '삭제' : 'Delete'}
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* 페이지네이션 */}
      <div className="transactions-pagination">
        <button 
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        >
          &laquo; {language === 'KR' ? '이전' : 'Prev'}
        </button>
        
        <span className="page-info">
          {language === 'KR' 
            ? `${currentPage} / ${Math.ceil(totalCount / pageSize)} 페이지`
            : `Page ${currentPage} of ${Math.ceil(totalCount / pageSize)}`
          }
        </span>
        
        <button 
          disabled={currentPage >= Math.ceil(totalCount / pageSize)}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          {language === 'KR' ? '다음' : 'Next'} &raquo;
        </button>
      </div>
      
      {/* 트랜잭션 폼 모달 */}
      {showForm && (
        <div className="transaction-form-modal">
          <div className="modal-overlay" onClick={() => setShowForm(false)}></div>
          <div className="modal-content">
            <button className="close-modal" onClick={() => setShowForm(false)}>
              &times;
            </button>
            <TransactionForm 
              user={user}
              language={language}
              SERVER_ROOT={SERVER_ROOT}
              settings={settings}
              accounts={accounts}
              categories={categories}
              onSubmit={handleFormSubmit}
              transaction={editingTransaction}
            />
          </div>
        </div>
      )}

      {/* 트랜잭션 폼 모달 뒤에 영수증 모달 추가 */}
      {showReceiptModal && currentReceipt && (
        <div className="receipt-modal">
          <div className="modal-overlay" onClick={() => setShowReceiptModal(false)}></div>
          <div className="modal-content receipt-modal-content">
            <button className="close-modal" onClick={() => setShowReceiptModal(false)}>
              &times;
            </button>
            <h3>{language === 'KR' ? '영수증 보기' : 'Receipt Details'}</h3>
            
            <div className="receipt-gallery">
              {currentReceipt.receipts.map((receipt, index) => (
                <div key={receipt.attachment_id} className="receipt-item">
                  <div className="receipt-image-container">
                    <img 
                      src={`${SERVER_ROOT}${receipt.file_path}`} 
                      alt={`Receipt ${index + 1}`} 
                      className="receipt-image"
                    />
                  </div>
                  {receipt.merchant_name && (
                    <div className="receipt-details">
                      <p><strong>{language === 'KR' ? '가맹점' : 'Merchant'}:</strong> {receipt.merchant_name}</p>
                      {receipt.receipt_date && (
                        <p><strong>{language === 'KR' ? '날짜' : 'Date'}:</strong> {formatDate(receipt.receipt_date)}</p>
                      )}
                      {receipt.receipt_total && (
                        <p><strong>{language === 'KR' ? '금액' : 'Amount'}:</strong> {formatCurrency(receipt.receipt_total, receipt.currency)}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionList;